import { useDeleteScan, useJobScans, useStationName } from '../../api/hooks';
import { useSession } from '../../auth/useUser';
import { Card, CardHeader } from '../../components/ui';

/**
 * A job's scan evidence, newest first. Shown twice on purpose: boxed into a scrolling
 * panel at the foot of the Plan page, where it is context beside the plan, and at full
 * length on the job's own Scans page, where it is the point of the page.
 */
export function ScanHistory({ jobId, full = false }: { jobId: string; full?: boolean }) {
  const scans = useJobScans(jobId);
  const stationName = useStationName();
  const removeScan = useDeleteScan(jobId);
  const session = useSession();
  const canDelete = session.can('admin');
  const rows = scans.data?.scans ?? [];

  return (
    <Card>
      <CardHeader
        title="Scan history"
        subtitle="Evidence, newest first. A scan proves a part reached a station; it never moves the plan."
      />
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-ink-500">
          Nothing scanned yet. Most parts carry no label, so this is expected.
        </p>
      ) : (
        <div className={full ? undefined : 'max-h-80 overflow-y-auto'}>
          <table className="w-full text-sm">
            <tbody>
              {rows.map((scan) => (
                <tr key={scan._id} className="border-b border-ink-100 last:border-0">
                  <td className="px-4 py-1.5 text-ink-600">{new Date(scan.at).toLocaleString()}</td>
                  <td className="px-4 py-1.5">
                    {scan.partCode ?? scan.qrText}
                    {scan.componentCode ? ` · component ${scan.componentCode}` : ''}
                    {/* The code identifies the label; the name is what the shop calls
                        the cabinet, and reading evidence without it is a lookup. */}
                    {scan.partName ? (
                      <span className="block text-xs text-ink-600">{scan.partName}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-1.5 text-ink-600">
                    {scan.stationKey ? stationName(scan.stationKey) : '—'}
                  </td>
                  <td className="px-4 py-1.5 text-ink-500">{scan.user}</td>
                  {canDelete ? (
                    <td className="px-4 py-1.5 text-right">
                      <button
                        type="button"
                        className="btn-ghost px-1.5 py-0.5 text-xs text-red-700"
                        onClick={() => {
                          if (confirm('Delete this scan? This only removes the evidence — it never changes the plan.')) {
                            removeScan.mutate(scan._id);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
